import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  MapPin, 
  Phone, 
  Mail, 
  History, 
  Calendar, 
  IdCard, 
  CheckCircle, 
  Package, 
  Truck, 
  RotateCw,
  Save,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  RefreshCw,
  Check,
  Key,
  Smartphone,
  ShieldCheck,
  Terminal,
  Copy,
  ExternalLink,
  ChevronRight,
  Info,
  FileText
} from 'lucide-react';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { db, doc, secureGetDoc, secureSetDoc, collection, getDocs, query, where } from '../firebase';
import { OrderRecord } from '../types';
import { formatThaiDateTime } from '../lib/dateUtils';
import { ThaiAddress } from '../data/thaiAddresses';
import { searchThaiAddress, parseThaiAddress } from '../lib/thaiAddressesDb';

interface WebhookLogItem {
  id: string;
  timestamp: string;
  type: 'incoming' | 'reply_ok' | 'reply_error' | 'push_ok' | 'push_error' | 'bind' | 'verify' | 'unknown';
  message: string;
  details?: any;
}

interface DiagnosticResponse {
  success: boolean;
  environment: {
    hasChannelAccessToken: boolean;
    hasChannelSecret: boolean;
    tokenLength: number;
    secretLength: number;
    nodeEnv: string;
  };
  lineOaId: string;
  appUrl: string;
  recentLogs: WebhookLogItem[];
  errors: string[];
}

interface CustomerDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any; // User object from Firestore or Auth
}

export const CustomerDashboardModal: React.FC<CustomerDashboardModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'history' | 'line' | 'policies'>('profile');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [address3, setAddress3] = useState('');
  const [lineUserId, setLineUserId] = useState('');
  const [orderHistory, setOrderHistory] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Autocomplete suggestions for address
  const [addressSuggestions, setAddressSuggestions] = useState<ThaiAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Form validation tracking
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Form field validity checkers
  const cleanPhone = phoneNumber.replace(/\D/g, '');
  const isFullNameValid = fullName.trim().length > 0;
  const isPhoneValid = cleanPhone.length >= 9 && cleanPhone.length <= 10;
  const isAddress1Valid = address1.trim().length > 0;
  const isAddress2Valid = address2.trim().length > 0;
  const isAddress3Valid = address3.trim().length === 5 && /^\d{5}$/.test(address3.trim());
  const isFormValid = isFullNameValid && isPhoneValid && isAddress1Valid && isAddress2Valid && isAddress3Valid;

  const handlePart2Change = async (val: string) => {
    setAddress2(val);
    if (val.trim().length >= 2) {
      const res = await searchThaiAddress(val);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePart3Change = async (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 5);
    setAddress3(cleaned);
    if (cleaned.length >= 2) {
      const res = await searchThaiAddress(cleaned);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectAddress = (item: ThaiAddress) => {
    setAddress2(`ต.${item.s} อ.${item.d} จ.${item.p}`);
    setAddress3(item.z);
    setShowSuggestions(false);
  };

  // LINE Binding & Diagnostic states
  const [manualLineId, setManualLineId] = useState('');
  const [savingLineBinding, setSavingLineBinding] = useState(false);
  const [checkingLineStatus, setCheckingLineStatus] = useState(false);
  const [lineModalMsg, setLineModalMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [diagnosticData, setDiagnosticData] = useState<DiagnosticResponse | null>(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [lineSubTab, setLineSubTab] = useState<'connect' | 'diagnostic'>('connect');

  const fetchProfileAndOrders = async () => {
    if (!user) return;
    try {
      setLoading(true);
      // Fetch profile
      const profileDocRef = doc(db, 'users', user.id, 'private', 'profile');
      const profileSnap = await secureGetDoc(profileDocRef, `users/${user.id}/private/profile`);
      if (profileSnap && profileSnap.exists()) {
        const profileData = profileSnap.data() as any;
        setFullName(profileData.fullName || '');
        setPhoneNumber(profileData.phoneNumber || '');
        const fullAddr = profileData.shippingAddress || '';
        setShippingAddress(fullAddr);

        let a1 = profileData.address1 || '';
        let a2 = profileData.address2 || '';
        let a3 = profileData.address3 || '';
        if (!a1 && !a2 && !a3 && fullAddr) {
          const parsed = parseThaiAddress(fullAddr);
          a1 = parsed.addr1;
          a2 = parsed.addr2;
          a3 = parsed.addr3;
        }
        setAddress1(a1);
        setAddress2(a2);
        setAddress3(a3);
        setLineUserId(profileData.lineUserId || '');
      }

      // Fetch user's orders from API with strict customer isolation
      const targetCustomerId = user.customerId ? String(user.customerId).trim() : '';
      const targetIdUpper = targetCustomerId.toUpperCase();
      const targetIdNoMem = targetIdUpper.replace(/^MEM/i, '');
      const userEmailLower = (user.email || '').toLowerCase();

      const queryParam = targetCustomerId 
        ? `?customerId=${encodeURIComponent(targetCustomerId)}${userEmailLower ? `&email=${encodeURIComponent(userEmailLower)}` : ''}` 
        : (userEmailLower ? `?email=${encodeURIComponent(userEmailLower)}` : '');

      const res = await fetch(`/api/orders${queryParam}`);
      if (res.ok) {
        const data = await res.json();
        const userOrders = (data.orders || []).filter((o: any) => {
          const oCustId = (o.customerId || o.memberId || '').toString().trim().toUpperCase();
          const oCustNoMem = oCustId.replace(/^MEM/i, '');
          const oEmailLower = (o.customerEmail || o.email || '').toString().trim().toLowerCase();

          if (targetIdNoMem && oCustNoMem && oCustNoMem === targetIdNoMem) {
            return true;
          }
          if (userEmailLower && oEmailLower && oEmailLower === userEmailLower) {
            return true;
          }
          if (user.id && o.userId && o.userId === user.id) {
            return true;
          }
          return false;
        });
        setOrderHistory(userOrders);
      }
    } catch (err) {
      console.error('Failed to load profile and orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncLineBindingStatus = async (showToast = false) => {
    if (!user) return;
    try {
      setCheckingLineStatus(true);
      const targetMemberId = user.customerId || user.id;
      const res = await fetch(`/api/line/status?memberId=${encodeURIComponent(targetMemberId)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.isBound && data.lineUserId) {
          setLineUserId(data.lineUserId);
          if (showToast) {
            setLineModalMsg({ type: 'success', text: `ตรวจพบการผูก LINE สำเร็จ! (ID: ${data.lineUserId.substring(0, 10)}...)` });
          }
          return;
        }
      }

      // Fallback check in Firestore
      const profileDocRef = doc(db, 'users', user.id, 'private', 'profile');
      const snap = await secureGetDoc(profileDocRef, `users/${user.id}/private/profile`);
      if (snap && snap.exists()) {
        const d = snap.data() as any;
        if (d.lineUserId) {
          setLineUserId(d.lineUserId);
          if (showToast) {
            setLineModalMsg({ type: 'success', text: `ตรวจพบการผูก LINE สำเร็จ! (ID: ${d.lineUserId.substring(0, 10)}...)` });
          }
          return;
        }
      }

      if (showToast) {
        setLineModalMsg({ type: 'error', text: 'ยังไม่พบการผูกบัญชี LINE กรุณาส่งรหัสทางแชท LINE @997wvsow' });
      }
    } catch (err: any) {
      console.error('Failed to sync LINE status:', err);
      if (showToast) {
        setLineModalMsg({ type: 'error', text: 'เกิดข้อผิดพลาดในการตรวจสอบสถานะ LINE' });
      }
    } finally {
      setCheckingLineStatus(false);
    }
  };

  const fetchDiagnostic = async () => {
    try {
      setLoadingDiagnostic(true);
      const res = await fetch('/api/line/diagnostic');
      if (res.ok) {
        const data = await res.json();
        setDiagnosticData(data);
      }
    } catch (err) {
      console.error('Failed to fetch diagnostic info:', err);
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  const handleDirectBind = async () => {
    if (!manualLineId.trim()) {
      setLineModalMsg({ type: 'error', text: 'กรุณากรอก LINE User ID ที่ขึ้นต้นด้วย U' });
      return;
    }
    const cleanId = manualLineId.trim();
    if (!cleanId.startsWith('U') || cleanId.length < 10) {
      setLineModalMsg({ type: 'error', text: 'LINE User ID ไม่ถูกต้อง (ต้องขึ้นต้นด้วย U ความยาว 33 ตัวอักษร)' });
      return;
    }

    try {
      setSavingLineBinding(true);
      setLineModalMsg(null);
      const targetMemberId = user.customerId || user.id;

      const res = await fetch('/api/line/direct-bind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId: targetMemberId,
          lineUserId: cleanId
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'บันทึกไม่สำเร็จ');
      }

      // Also persist to Firestore
      const profileDocRef = doc(db, 'users', user.id, 'private', 'profile');
      await secureSetDoc(profileDocRef, {
        lineUserId: cleanId,
        updatedAt: new Date().toISOString()
      }, `users/${user.id}/private/profile`);

      setLineUserId(cleanId);
      setManualLineId('');
      setLineModalMsg({ type: 'success', text: 'บันทึกผูก LINE User ID สำเร็จเรียบร้อยแล้ว!' });
    } catch (err: any) {
      setLineModalMsg({ type: 'error', text: err?.message || 'เกิดข้อผิดพลาดในการบันทึก LINE ID' });
    } finally {
      setSavingLineBinding(false);
    }
  };

  const copyWebhookUrl = (url: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 3000);
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchProfileAndOrders();
      syncLineBindingStatus();
      setMessage('');
      setError('');
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    // Strict validation of mandatory fields (*)
    if (!fullName.trim()) {
      setError('กรุณากรอก "ชื่อ-นามสกุลจริง สมาชิก" (* บังคับกรอก)');
      return;
    }
    const cleanP = phoneNumber.replace(/\D/g, '');
    if (!cleanP || cleanP.length < 9 || cleanP.length > 10) {
      setError('กรุณากรอก "เบอร์โทรศัพท์ติดต่อรับพัสดุ" ให้ถูกต้อง 9-10 หลัก (* บังคับกรอก)');
      return;
    }
    if (!address1.trim()) {
      setError('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 1: "บ้านเลขที่ ถนน ซอย / หมู่บ้านอาคาร" (* บังคับกรอก)');
      return;
    }
    if (!address2.trim()) {
      setError('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 2: "ตำบล/อำเภอ/จังหวัด" (* บังคับกรอก)');
      return;
    }
    if (!address3.trim() || address3.trim().length !== 5 || !/^\d{5}$/.test(address3.trim())) {
      setError('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 3: "รหัสไปรษณีย์ 5 หลัก" ให้ถูกต้อง (* บังคับกรอก)');
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');

    try {
      const combinedAddress = [address1.trim(), address2.trim(), address3.trim()].filter(Boolean).join(' ');
      const profileDocRef = doc(db, 'users', user.id, 'private', 'profile');
      await secureSetDoc(profileDocRef, {
        userId: user.id,
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        shippingAddress: combinedAddress,
        address1: address1.trim(),
        address2: address2.trim(),
        address3: address3.trim(),
        updatedAt: new Date().toISOString()
      }, `users/${user.id}/private/profile`);
      
      setShippingAddress(combinedAddress);
      setHasAttemptedSubmit(false);
      setMessage('บันทึกการแก้ไขโปรไฟล์และข้อมูลการจัดส่งสำเร็จ!');
    } catch (err: any) {
      setError(err?.message || 'ไม่สามารถบันทึกข้อมูลได้');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto">
      <div className="relative w-full max-w-4xl my-auto bg-white rounded-3xl shadow-2xl border border-blue-200/50 flex flex-col max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-blue-100 bg-gradient-to-r from-blue-50/70 via-white to-blue-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-[#2A59FE] to-[#1E3A8A] text-white flex items-center justify-center shadow-sm">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black text-[#0E2C58]">
                แดชบอร์ดสมาชิกกิฟฟารีน
              </h2>
              <p className="text-xs text-slate-500">
                ยินดีต้อนรับคุณ {fullName || user.email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick LINE button on header for mobile & desktop */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('line');
                syncLineBindingStatus();
                fetchDiagnostic();
              }}
              className="px-3 py-1.5 bg-[#06C755] hover:bg-[#05b34c] text-white font-extrabold text-xs rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95 border border-emerald-400/30"
              title="ผูก LINE รับแจ้งเตือน & เช็คสถานะ Webhook"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">{lineUserId ? 'LINE: ผูกแล้ว' : 'ผูก LINE แจ้งเตือน'}</span>
              <span className="sm:hidden">{lineUserId ? 'LINE แล้ว' : 'ผูก LINE'}</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Selection (Responsive Scrollable Tabs) */}
        <div className="px-4 sm:px-6 py-2 bg-slate-50 border-b border-blue-100 flex gap-2 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
              activeTab === 'profile'
                ? 'bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white shadow-xs'
                : 'text-slate-600 hover:text-[#2A59FE] hover:bg-blue-50'
            }`}
          >
            <IdCard className="w-4 h-4" />
            <span>จัดการโปรไฟล์</span>
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
              activeTab === 'history'
                ? 'bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white shadow-xs'
                : 'text-slate-600 hover:text-[#2A59FE] hover:bg-blue-50'
            }`}
          >
            <History className="w-4 h-4" />
            <span>ประวัติคำสั่งซื้อ ({orderHistory.length})</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('line');
              syncLineBindingStatus();
              fetchDiagnostic();
            }}
            className={`px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
              activeTab === 'line'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            <MessageSquare className="w-4 h-4 text-[#06C755]" />
            <span>แจ้งเตือน LINE & ตรวจสอบระบบ</span>
            {lineUserId ? (
              <span className="text-[10px] px-1.5 py-0.2 bg-emerald-700 text-white rounded-md font-bold">
                ผูกแล้ว
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.2 bg-amber-200 text-amber-900 rounded-md font-bold">
                ยังไม่ผูก
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('policies')}
            className={`px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
              activeTab === 'policies'
                ? 'bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white shadow-xs'
                : 'text-slate-600 hover:text-[#2A59FE] hover:bg-blue-50'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-[#2A59FE]" />
            <span>เกี่ยวกับเรา & นโยบายร้านค้า</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/30">
          {message && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-2 text-emerald-700 text-xs font-semibold">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{message}</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-2 text-rose-700 text-xs font-semibold">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-2 text-[#2A59FE]">
              <RotateCw className="w-8 h-8 animate-spin" />
              <span className="text-xs text-slate-500 font-bold">กำลังดึงข้อมูลธุรกรรม...</span>
            </div>
          ) : activeTab === 'profile' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              {/* Member Card Component */}
              <div className="md:col-span-1 bg-gradient-to-br from-[#2A59FE] via-[#1E3A8A] to-[#0E2C58] rounded-3xl p-5 text-white shadow-lg shadow-blue-900/10 border border-blue-400/20">
                <div className="flex justify-between items-start mb-4">
                  <div className="text-[10px] font-bold tracking-widest text-blue-100 uppercase">
                    Giffarine Premium Member
                  </div>
                  <Package className="w-5 h-5 text-blue-200" />
                </div>
                <div className="mb-6">
                  <div className="text-[11px] text-blue-200">Customer ID บัญชีของฉัน</div>
                  <div className="text-2xl font-black tracking-wider font-mono">
                    {user.customerId || 'Generating...'}
                  </div>
                </div>
                <div className="space-y-2 text-xs text-blue-100">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>สมัครเมื่อ: {new Date(user.createdAt).toLocaleDateString('th-TH')}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1 border-t border-blue-400/30">
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>สถานะ LINE: {lineUserId ? 'ผูกบัญชีเรียบร้อย' : 'ยังไม่ผูกบัญชี'}</span>
                  </div>
                </div>
              </div>

              {/* Form editing profile details */}
              <form onSubmit={handleUpdateProfile} className="md:col-span-2 space-y-4 bg-white p-5 sm:p-6 rounded-3xl border border-blue-100 shadow-2xs">
                <div className="flex items-center justify-between border-b border-blue-100/70 pb-2 mb-3">
                  <h3 className="text-sm font-black text-[#0E2C58] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#2A59FE]" />
                    <span>ข้อมูลประวัติและที่อยู่จัดส่งพัสดุสะสมแต้ม PV</span>
                  </h3>
                  <span className="text-[10px] text-slate-400">ระบบคลาวด์เข้ารหัสความปลอดภัย</span>
                </div>
                
                {/* 1. Full Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    ชื่อ-นามสกุลจริง สมาชิก <span className="text-rose-500 font-bold ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="กรอกชื่อ-นามสกุลจริง"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                        hasAttemptedSubmit && !isFullNameValid
                          ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                          : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                      }`}
                    />
                    <User className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                  </div>
                  {hasAttemptedSubmit && !isFullNameValid && (
                    <span className="text-[10px] font-semibold text-rose-500 mt-1 block">
                      * กรุณากรอกชื่อ-นามสกุลจริง สมาชิก (จำเป็น)
                    </span>
                  )}
                </div>

                {/* 2. Phone Number */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    เบอร์โทรศัพท์ติดต่อรับพัสดุ <span className="text-rose-500 font-bold ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      required
                      value={phoneNumber}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(digits);
                      }}
                      placeholder="เช่น 0812345678 (9-10 หลัก)"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                        hasAttemptedSubmit && !isPhoneValid
                          ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                          : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                      }`}
                    />
                    <Phone className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                  </div>
                  {hasAttemptedSubmit && !isPhoneValid && (
                    <span className="text-[10px] font-semibold text-rose-500 mt-1 block">
                      * กรุณากรอกเบอร์โทรศัพท์ติดต่อ 9-10 หลัก (จำเป็น)
                    </span>
                  )}
                </div>

                {/* 3. Shipping Address 3 structured rows */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    ที่อยู่จัดส่งสินค้าพัสดุสะสม <span className="text-rose-500 font-bold ml-0.5">*</span>
                  </label>
                  <div className="space-y-2 relative">
                    {/* ช่องที่ 1 */}
                    <div>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          value={address1}
                          onChange={(e) => setAddress1(e.target.value)}
                          placeholder="ช่องที่ 1: บ้านเลขที่ ถนน ซอย / หมู่บ้านอาคาร"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                            hasAttemptedSubmit && !isAddress1Valid
                              ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                              : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                          }`}
                        />
                        <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                      </div>
                      {hasAttemptedSubmit && !isAddress1Valid && (
                        <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                          * กรุณากรอกบ้านเลขที่ ถนน ซอย (จำเป็น)
                        </span>
                      )}
                    </div>

                    {/* ช่องที่ 2 */}
                    <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          required
                          value={address2}
                          onChange={(e) => handlePart2Change(e.target.value)}
                          onFocus={() => {
                            if (addressSuggestions.length > 0) setShowSuggestions(true);
                          }}
                          placeholder="ช่องที่ 2: ตำบล/อำเภอ/จังหวัด"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                            hasAttemptedSubmit && !isAddress2Valid
                              ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                              : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                          }`}
                        />
                        <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                      </div>
                      {hasAttemptedSubmit && !isAddress2Valid && (
                        <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                          * กรุณากรอกตำบล อำเภอ และจังหวัด (จำเป็น)
                        </span>
                      )}
                    </div>

                    {/* ช่องที่ 3 */}
                    <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          required
                          maxLength={5}
                          value={address3}
                          onChange={(e) => handlePart3Change(e.target.value)}
                          onFocus={() => {
                            if (addressSuggestions.length > 0) setShowSuggestions(true);
                          }}
                          placeholder="ช่องที่ 3: รหัสไปรษณีย์ 5 หลัก"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm font-mono transition-all ${
                            hasAttemptedSubmit && !isAddress3Valid
                              ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                              : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                          }`}
                        />
                        <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                      </div>
                      {hasAttemptedSubmit && !isAddress3Valid && (
                        <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                          * กรุณากรอกรหัสไปรษณีย์ 5 หลักให้ถูกต้อง (จำเป็น)
                        </span>
                      )}

                      {/* Suggestions dropdown */}
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-pink-200 rounded-xl shadow-xl max-h-48 overflow-y-auto z-30 divide-y divide-pink-50">
                          {addressSuggestions.map((item, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectAddress(item)}
                              className="w-full text-left px-3.5 py-2 hover:bg-pink-50 transition-colors flex items-center justify-between text-xs text-slate-700 cursor-pointer"
                            >
                              <span>
                                <strong className="text-pink-600">ต.{item.s}</strong> อ.{item.d} จ.{item.p}
                              </span>
                              <span className="font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded text-[11px]">
                                {item.z}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer validation notice and action */}
                <div className="pt-2 border-t border-pink-100 space-y-2">
                  {!isFormValid && (
                    <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 text-[11px] text-amber-800 flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                      <span>
                        <strong>เงื่อนไขสำคัญ:</strong> ข้อมูลที่มีเครื่องหมาย <span className="text-rose-600 font-bold">(*)</span> กำกับ ต้องบังคับกรอกให้ครบทุกช่อง หากกรอกไม่ครบจะไม่สามารถบันทึกข้อมูลได้
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={saving || !isFormValid}
                      className="px-6 py-2.5 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 shadow-sm active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all"
                      title={!isFormValid ? 'กรุณากรอกข้อมูลที่มี * ให้ครบทุกช่องก่อนบันทึก' : 'บันทึกข้อมูลการจัดส่ง'}
                    >
                      <Save className="w-4 h-4" />
                      <span>{saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูลการจัดส่ง'}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          ) : activeTab === 'history' ? (
            /* Order History Tab */
            <div className="space-y-4">
              <h3 className="text-sm font-black text-[#0E2C58] border-b border-blue-100 pb-2">
                ประวัติการสั่งซื้อพัสดุของคุณในระบบ
              </h3>

              {orderHistory.length === 0 ? (
                <div className="py-12 flex flex-col items-center justify-center text-slate-400 gap-2">
                  <Package className="w-12 h-12 text-blue-200" />
                  <span className="text-sm font-bold">ไม่พบประวัติการสั่งซื้อของคุณในระบบ</span>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-300 shadow-3xs bg-white">
                  <table className="w-full text-left border-collapse border border-slate-300 text-xs min-w-[780px]">
                    <thead>
                      <tr className="bg-slate-50 text-slate-900 font-bold border-b border-slate-300 divide-x divide-slate-300">
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[130px]">วันที่</th>
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[130px]">หมายเลขคำสั่งซื้อ</th>
                        <th className="p-2.5 border border-slate-300 min-w-[220px]">รายการสินค้า</th>
                        <th className="p-2.5 border border-slate-300 text-right whitespace-nowrap min-w-[80px]">ราคา</th>
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[100px]">ช่องทางชำระเงิน</th>
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[90px]">คะแนนสะสม</th>
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[110px]">สถานะจัดส่ง</th>
                        <th className="p-2.5 border border-slate-300 text-center whitespace-nowrap min-w-[130px]">หมายเลขติดตามพัสดุ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300 bg-white">
                      {orderHistory.map((order) => (
                        <tr key={order.orderId} className="hover:bg-blue-50/30 transition-colors divide-x divide-slate-300">
                          {/* 1. วันที่ */}
                          <td className="p-2.5 border border-slate-300 text-slate-700 font-medium text-center whitespace-nowrap font-mono">
                            {formatThaiDateTime(order.timestamp)}
                          </td>

                          {/* 2. หมายเลขคำสั่งซื้อ */}
                          <td className="p-2.5 border border-slate-300 font-mono font-bold text-[#2A59FE] text-center whitespace-nowrap">
                            {order.orderId}
                          </td>

                          {/* 3. รายการสินค้า */}
                          <td className="p-2.5 border border-slate-300 text-slate-800">
                            <div className="space-y-1">
                              {order.items && order.items.length > 0 ? (
                                order.items.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-start gap-2 leading-relaxed">
                                    <span className="text-slate-700">• {item.productName}</span>
                                    <span className="text-slate-900 font-bold shrink-0">x{item.quantity}</span>
                                  </div>
                                ))
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </div>
                          </td>

                          {/* 4. ราคา */}
                          <td className="p-2.5 border border-slate-300 text-right font-bold text-slate-900 whitespace-nowrap">
                            ฿{order.totalAmount?.toLocaleString()}
                          </td>

                          {/* 5. ช่องทางชำระเงิน */}
                          <td className="p-2.5 border border-slate-300 text-center font-semibold text-slate-700 whitespace-nowrap">
                            {order.paymentMethod === 'COD' 
                              ? 'COD' 
                              : order.paymentMethod === 'BANK_TRANSFER' 
                                ? 'โอนเงินธนาคาร' 
                                : order.paymentMethod || '-'}
                          </td>

                          {/* 6. คะแนนสะสม */}
                          <td className="p-2.5 border border-slate-300 text-center font-bold text-amber-600 whitespace-nowrap">
                            {order.totalPv || 0} PV
                          </td>

                          {/* 7. สถานะจัดส่ง */}
                          <td className="p-2.5 border border-slate-300 text-center whitespace-nowrap">
                            <span className={`inline-block px-2 py-0.5 rounded font-bold text-[11px] ${
                              order.orderStatus === 'Shipped' || order.orderStatus === 'Completed'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : order.orderStatus === 'Pending_Shipment'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-slate-100 text-slate-700 border border-slate-200'
                            }`}>
                              {order.orderStatus === 'Pending_Shipment' 
                                ? 'Pending_Shipment' 
                                : order.orderStatus === 'Shipped' 
                                  ? 'Shipped' 
                                  : order.orderStatus === 'Completed'
                                    ? 'Completed'
                                    : order.orderStatus || '-'}
                            </span>
                          </td>

                          {/* 8. หมายเลขติดตามพัสดุ */}
                          <td className="p-2.5 border border-slate-300 text-center font-mono font-semibold text-slate-700 whitespace-nowrap">
                            {order.trackingNumber ? (
                              <span className="text-blue-600 font-bold">{order.trackingNumber}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : activeTab === 'line' ? (
            /* LINE Binding Tab */
            <div className="space-y-5 bg-white p-4 sm:p-6 rounded-3xl border border-emerald-100 shadow-2xs">
              {lineModalMsg && (
                <div className={`p-3.5 rounded-2xl text-xs font-bold flex items-center gap-2 ${
                  lineModalMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  {lineModalMsg.type === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  )}
                  <span>{lineModalMsg.text}</span>
                </div>
              )}

              <div className="space-y-5">
                {/* Status Banner */}
                <div className={`p-4 rounded-2xl border flex items-center justify-between gap-3 ${
                  lineUserId ? 'bg-emerald-50/80 border-emerald-200' : 'bg-amber-50/80 border-amber-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${
                      lineUserId ? 'bg-[#06C755]' : 'bg-amber-500'
                    }`}>
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-500">สถานะการผูกบัญชี LINE</div>
                      <div className="text-sm font-black text-slate-800">
                        {lineUserId ? 'ผูกบัญชีเรียบร้อย พร้อมรับแจ้งเตือนอัตโนมัติ' : 'ยังไม่ได้ผูกบัญชี LINE'}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={checkingLineStatus}
                    onClick={() => syncLineBindingStatus(true)}
                    className="px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl border border-slate-200 shadow-2xs transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${checkingLineStatus ? 'animate-spin' : ''}`} />
                    <span>{checkingLineStatus ? 'กำลังตรวจ...' : 'ตรวจสอบสถานะ'}</span>
                  </button>
                </div>

                {/* 3 Simple Steps */}
                <div className="space-y-3">
                  <div className="text-xs font-black text-slate-800 uppercase tracking-wider">
                    ขั้นตอนการผูกบัญชี LINE รับแจ้งเตือนคำสั่งซื้อ:
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Step 1 */}
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-2 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        <div className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs font-bold flex items-center justify-center">1</div>
                        <div className="text-xs font-black text-slate-800">เพิ่มเพื่อน LINE Official Account</div>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          กดเพิ่มเพื่อนเพื่อเปิดรับการแจ้งเตือน (LINE ID: <strong className="text-emerald-700">@997wvsow</strong>)
                        </p>
                      </div>
                      <div className="pt-1 flex justify-center">
                        <a
                          href="https://lin.ee/PSM7qHqg"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block transition-transform hover:scale-[1.03] active:scale-95"
                        >
                          <img 
                            src="https://scdn.line-apps.com/n/line_add_friends/btn/th.png" 
                            alt="เพิ่มเพื่อน" 
                            height="36" 
                            style={{ border: 0, height: '36px' }} 
                          />
                        </a>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-2">
                      <div className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs font-bold flex items-center justify-center">2</div>
                      <div className="text-xs font-black text-slate-800">ส่งรหัสสมาชิกลงแชท</div>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        พิมพ์รหัสของคุณในช่องแชท LINE:
                      </p>
                      <div className="font-mono font-black text-xs text-[#2A59FE] bg-blue-50/80 px-2 py-1.5 rounded-lg border border-blue-200 text-center select-all">
                        {user.customerId || user.id}
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-2">
                      <div className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs font-bold flex items-center justify-center">3</div>
                      <div className="text-xs font-black text-slate-800">ระบบผูกอัตโนมัติ</div>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        ระบบจะตอบกลับและผูกบัญชีเพื่อส่งใบเสร็จและแจ้งเตือนพัสดุเข้า LINE ทันที
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'policies' ? (
            /* Tab 4: Submenu ข้อมูล & นโยบายร้านค้า (Footer Submenu) */
            <div className="space-y-6">
              {/* Header / Brand Capsule */}
              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-3xs space-y-4">
                <div className="flex items-center justify-between">
                  {/* Official Giffarine Pill Badge Capsule */}
                  <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-slate-600 shadow-sm shadow-slate-900/10 border border-slate-500">
                    <div className="w-6 h-6 rounded-md bg-white p-0.5 overflow-hidden flex items-center justify-center shrink-0 shadow-2xs">
                      <img
                        src="/icon.png"
                        alt="Giffarine Official App Icon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="h-3.5 w-px bg-white/50" />
                    <img
                      src={GIFFARINE_OFFICIAL_LOGO}
                      alt="GIFFARINE"
                      className="h-4 w-auto object-contain pr-1 brightness-0 invert"
                    />
                  </div>

                  <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                    ศูนย์ข้อมูลสมาชิก
                  </span>
                </div>

                {/* Policies & Info Navigation Menu List */}
                <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden bg-white">
                  {/* 1. About Us */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/aboutus');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-blue-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#2A59FE]">
                      <span className="w-2 h-2 rounded-full bg-slate-300 group-hover:bg-[#2A59FE] transition-colors" />
                      <span>เกี่ยวกับเรา (About Us)</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2A59FE] group-hover:translate-x-0.5 transition-all" />
                  </button>

                  {/* 2. Member Privilege */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/member-privilege');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-rose-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#DB2777]">
                      <span className="w-2 h-2 rounded-full bg-rose-400 group-hover:bg-[#DB2777] transition-colors" />
                      <span className="text-[#DB2777]">สมัครกิฟฟารีน (180 บาทตลอดชีพ)</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-rose-50 text-rose-600 font-extrabold px-2 py-0.5 rounded border border-rose-200">
                        ลด 25%
                      </span>
                      <ChevronRight className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </button>

                  {/* 3. Privacy Policy */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/privacy-policy');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-blue-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#2A59FE]">
                      <span className="w-2 h-2 rounded-full bg-slate-300 group-hover:bg-[#2A59FE] transition-colors" />
                      <span>นโยบายความเป็นส่วนตัว (Privacy Policy)</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2A59FE] group-hover:translate-x-0.5 transition-all" />
                  </button>

                  {/* 4. PDPA */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/pdpa');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-emerald-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-emerald-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 group-hover:bg-emerald-600 transition-colors" />
                      <span>การคุ้มครองข้อมูลส่วนบุคคล PDPA</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded border border-emerald-200">
                        PDPA
                      </span>
                      <ChevronRight className="w-4 h-4 text-emerald-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </button>

                  {/* 5. Cookie Policy */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/cookie-policy');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-blue-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#2A59FE]">
                      <span className="w-2 h-2 rounded-full bg-slate-300 group-hover:bg-[#2A59FE] transition-colors" />
                      <span>นโยบายการใช้คุกกี้ (Cookie Policy)</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2A59FE] group-hover:translate-x-0.5 transition-all" />
                  </button>

                  {/* 6. Cookie Settings */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/cookie-settings');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-blue-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#2A59FE]">
                      <span className="w-2 h-2 rounded-full bg-slate-300 group-hover:bg-[#2A59FE] transition-colors" />
                      <span>การตั้งค่าคุกกี้ (Cookie Settings)</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2A59FE] group-hover:translate-x-0.5 transition-all" />
                  </button>

                  {/* 7. Terms & Conditions */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/terms');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-blue-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-[#2A59FE]">
                      <span className="w-2 h-2 rounded-full bg-slate-300 group-hover:bg-[#2A59FE] transition-colors" />
                      <span>เงื่อนไขและข้อตกลงการใช้บริการ</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2A59FE] group-hover:translate-x-0.5 transition-all" />
                  </button>

                  {/* 8. Shipping & Return Policy */}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.history.pushState({}, '', '/shipping-policy');
                      window.dispatchEvent(new Event('popstate'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left p-3.5 hover:bg-rose-50/60 transition-colors flex items-center justify-between group cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5 text-xs font-bold text-slate-800 group-hover:text-rose-700">
                      <span className="w-2 h-2 rounded-full bg-rose-400 group-hover:bg-rose-600 transition-colors" />
                      <span>นโยบายจัดส่ง คืนสินค้า และประกัน 7 วัน</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-rose-50 text-rose-700 font-bold px-2 py-0.5 rounded border border-rose-200">
                        7 วัน
                      </span>
                      <ChevronRight className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </button>
                </div>
              </div>

              {/* About GIFFFORYOU.COM Card */}
              <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50/40 p-5 rounded-3xl border border-blue-100 shadow-3xs space-y-3">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#2A59FE]" />
                  <span>เกี่ยวกับ GIFFFORYOU.COM</span>
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  เว็บไซต์สั่งซื้อผลิตภัณฑ์กิฟฟารีนออนไลน์อย่างเป็นทางการ สำหรับสมาชิก นักธุรกิจ และผู้บริโภคทั่วไป มั่นใจสินค้าแท้ 100% ส่งตรงจากคลังสินค้ากิฟฟารีนทั่วประเทศ รวดเร็ว ปลอดภัย พร้อมรับประกันความพึงพอใจ 7 วัน
                </p>
                <div className="pt-2 flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-xl bg-blue-100/80 text-[#2A59FE] text-[11px] font-bold border border-blue-200">
                    ✓ สินค้าแท้ 100%
                  </span>
                  <span className="px-3 py-1 rounded-xl bg-emerald-100/80 text-emerald-800 text-[11px] font-bold border border-emerald-200">
                    ✓ ประกันคืนเงิน 7 วัน
                  </span>
                </div>
              </div>

              {/* Official Contact & LINE OA */}
              <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-3xs space-y-3">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#06C755]" />
                  <span>ติดต่อ & ปรึกษาสุขภาพ LINE OA</span>
                </h4>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  <div>
                    <span className="text-xs font-mono font-bold text-slate-800">
                      LINE ID: <strong className="text-emerald-700">@997wvsow</strong>
                    </span>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                      บริการจัดส่งด่วนทั่วประเทศ Flash / Kerry / ไปรษณีย์ไทย
                    </p>
                  </div>
                  <a 
                    href="https://lin.ee/PSM7qHqg" 
                    target="_blank" 
                    rel="noreferrer" 
                    className="inline-block transition-transform hover:scale-[1.03] active:scale-95"
                  >
                    <img 
                      src="https://scdn.line-apps.com/n/line_add_friends/btn/th.png" 
                      alt="เพิ่มเพื่อน" 
                      height="36" 
                      style={{ border: 0, height: '36px' }} 
                    />
                  </a>
                </div>
              </div>

              {/* Copyright */}
              <div className="text-center text-[11px] text-slate-400 py-2">
                © 2026 GIFFFORYOU.COM — Giffarine Official Online Store. All rights reserved.
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
